/**
 * BLRRD Text Tool — render.js  (Phase 4.G.vector, Wave 12.2)
 *
 * Rasterise a text-block model (src/text/model.js) onto a Canvas-2D
 * context and bake the result into an ACEScg-linear Float32 RGBA buffer
 * suitable for src/layers/model.js::createPixelLayer.
 *
 * Pipeline:
 *   1. Layout — split into lines, measure each via canvas `measureText`,
 *      stack them with the block's line-height. (W3C HTML Canvas 2D
 *      Context spec §11.4 fillText / measureText.)
 *   2. Draw — paint each line in OPAQUE WHITE on a transparent canvas
 *      using the resolved system-font stack. We draw white so the canvas'
 *      own (display-sRGB, non-colour-managed) text colour never has to be
 *      matched: the per-pixel alpha is the antialiased glyph coverage,
 *      and we tint with the user's ACEScg colour in step 3. This sidesteps
 *      sRGB↔ACEScg round-tripping the glyph fill and keeps text edges
 *      crisp.
 *   3. Bake — convert the canvas' coverage (alpha channel) into a full-
 *      canvas ACEScg RGBA buffer where every covered pixel carries the
 *      user's chosen ACEScg colour pre-multiplied by glyph coverage. The
 *      buffer is canvas-sized so it drops straight into a pixel layer at
 *      offset (0,0).
 *
 * The DOM-touching part (offscreen canvas + 2D context) is injected via a
 * `makeCanvas` factory so the module stays unit-testable in pure Node
 * (the test gate passes a stub canvas/ctx with deterministic measureText).
 *
 * Privacy: the font stack is resolved through model.js::resolveFontStack,
 * which rejects any non-system family. NO web fonts. NO external origins.
 *
 * Provenance: clean-room. Canvas-2D text APIs per the W3C spec. sRGB →
 * ACEScg via src/color/spaces.js. NO third-party text-shaping code.
 */

import { M_sRGB_to_ACEScg, decodeSRGB } from '../color/spaces.js';
import {
    resolveFontStack, isSystemFont, textLines, fontShorthand,
    DEFAULT_FONT_FAMILY, SYSTEM_FONTS,
} from './model.js';

/* ── Layout ──────────────────────────────────────────────────────────── */

/**
 * Measure a text block's layout. `measure(line)` must return an object
 * with at least `{ width }` (the canvas `measureText` shape). Pure: no
 * DOM access, deterministic given the measure callback.
 *
 * @param {object} block         text-block model
 * @param {(line:string)=>{width:number}} measure
 * @returns {{
 *   lines: Array<{ text:string, width:number, x:number, y:number }>,
 *   width: number, height: number, lineHeightPx: number, ascent: number
 * }}
 */
export function layoutTextBlock(block, measure) {
    const lines = textLines(block);
    const lineHeightPx = block.size * block.lineHeight;
    /* Canvas `textBaseline = 'alphabetic'` puts y at the baseline; we lay
     * out top-aligned, so the first baseline sits one ascent below y.
     * Without a measured font-ascent (Node stub) we approximate ascent as
     * 0.8 × size — the typical cap-to-baseline ratio. */
    const ascent = block.size * 0.8;
    let maxWidth = 0;
    const out = [];
    for (let i = 0; i < lines.length; i++) {
        const m = measure(lines[i]) || { width: 0 };
        const w = Number.isFinite(m.width) ? m.width : 0;
        if (w > maxWidth) maxWidth = w;
        out.push({ text: lines[i], width: w, x: 0, y: i * lineHeightPx + ascent });
    }
    /* Apply horizontal alignment relative to the block's own bounding box
     * (max line width). Canvas textAlign handles the draw-time anchor, but
     * we also record an x so the layout box is self-describing. */
    for (const ln of out) {
        if (block.align === 'center')      ln.x = (maxWidth - ln.width) / 2;
        else if (block.align === 'right')  ln.x = maxWidth - ln.width;
        else                               ln.x = 0;
    }
    return {
        lines: out,
        width: Math.ceil(maxWidth),
        height: Math.ceil(lines.length * lineHeightPx),
        lineHeightPx,
        ascent,
    };
}

/* ── sRGB8 (display) → ACEScg-linear ─────────────────────────────────── */

/**
 * Convert a single 8-bit sRGB triplet to ACEScg-linear. Reusable helper
 * shared with the colour-input parsing in the UI.
 *
 * @param {number} r8 0..255
 * @param {number} g8 0..255
 * @param {number} b8 0..255
 * @returns {[number,number,number]}
 */
export function srgb8ToAces(r8, g8, b8) {
    const lr = decodeSRGB((r8 & 255) / 255);
    const lg = decodeSRGB((g8 & 255) / 255);
    const lb = decodeSRGB((b8 & 255) / 255);
    const m = M_sRGB_to_ACEScg;
    return [
        m[0] * lr + m[1] * lg + m[2] * lb,
        m[3] * lr + m[4] * lg + m[5] * lb,
        m[6] * lr + m[7] * lg + m[8] * lb,
    ];
}

/* ── Rasterise to ACEScg Float32 RGBA ────────────────────────────────── */

/**
 * Render a text block into a canvas-sized ACEScg-linear Float32 RGBA
 * buffer.
 *
 * @param {object} block            text-block model (color is ACEScg RGBA)
 * @param {number} canvasW          target canvas width  (= layer width)
 * @param {number} canvasH          target canvas height (= layer height)
 * @param {() => { canvas:object, ctx:object }} makeCanvas
 *        factory returning an offscreen canvas + 2D context. In the browser
 *        this wraps document.createElement('canvas'); in tests a stub.
 * @returns {{ rgba: Float32Array, w: number, h: number,
 *             layout: object }}
 */
export function rasterizeTextBlock(block, canvasW, canvasH, makeCanvas) {
    const W = canvasW | 0, H = canvasH | 0;
    if (W <= 0 || H <= 0) throw new Error('rasterizeTextBlock: invalid canvas dims');
    if (typeof makeCanvas !== 'function') {
        throw new Error('rasterizeTextBlock: makeCanvas factory required');
    }
    /* Privacy gate — refuse non-system families outright. */
    if (!isSystemFont(block.fontFamily)) {
        throw new Error(
            `rasterizeTextBlock: font "${block.fontFamily}" is not a system font; ` +
            `web fonts are disallowed (privacy-first)`,
        );
    }

    const { canvas, ctx } = makeCanvas();
    canvas.width = W;
    canvas.height = H;
    ctx.clearRect(0, 0, W, H);
    ctx.font = fontShorthand(block);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = block.align;
    /* Draw glyphs in opaque white; coverage lives in the alpha channel. */
    ctx.fillStyle = '#ffffff';

    const layout = layoutTextBlock(block, (line) => ctx.measureText(line));

    /* Draw each line. The canvas textAlign anchor is the block's x plus an
     * alignment-dependent offset so the visible text box starts at block.x:
     *   left   → anchor at x
     *   center → anchor at x + width/2
     *   right  → anchor at x + width */
    let anchorX = block.x;
    if (block.align === 'center')     anchorX = block.x + layout.width / 2;
    else if (block.align === 'right') anchorX = block.x + layout.width;

    for (const ln of layout.lines) {
        ctx.fillText(ln.text, anchorX, block.y + ln.y);
    }

    /* Read back the rasterised coverage and tint into ACEScg. */
    const img = ctx.getImageData(0, 0, W, H);
    const src = img.data;                 /* Uint8ClampedArray sRGB RGBA */
    const out = new Float32Array(W * H * 4);

    const ar = Number(block.color[0]) || 0;
    const ag = Number(block.color[1]) || 0;
    const ab = Number(block.color[2]) || 0;
    const aa = block.color.length >= 4 ? (Number(block.color[3]) || 0) : 1;

    for (let i = 0; i < W * H; i++) {
        const j = i * 4;
        /* Glyph coverage = white-text alpha (0..255). The RGB is white so
         * we ignore it and substitute the user's ACEScg colour. */
        const cov = src[j + 3] / 255;
        if (cov <= 0) continue;           /* leave transparent (already 0) */
        out[j]     = ar;
        out[j + 1] = ag;
        out[j + 2] = ab;
        out[j + 3] = aa * cov;            /* non-premultiplied alpha */
    }

    return { rgba: out, w: W, h: H, layout };
}

/* ── Bounding box (image-pixel coords) ───────────────────────────────── */

/**
 * Compute the text block's bounding box in image-pixel coordinates given
 * a measure callback. Pure; used by the UI to draw the placement caret +
 * by the test gate to assert measureText-driven sizing.
 *
 * @returns {{ x:number, y:number, w:number, h:number }}
 */
export function textBlockBounds(block, measure) {
    const layout = layoutTextBlock(block, measure);
    /* Top of the box is block.y; height spans all lines. The x origin
     * shifts with alignment so the box always wraps the visible glyphs. */
    let x = block.x;
    if (block.align === 'center')     x = block.x;          /* box still starts at x */
    else if (block.align === 'right') x = block.x;
    return { x, y: block.y, w: layout.width, h: layout.height };
}

/* Re-export the font allow-list so consumers don't have to dual-import. */
export { SYSTEM_FONTS, resolveFontStack, DEFAULT_FONT_FAMILY };
